import { useLazyQuery } from '@apollo/client'
import { useState } from 'react'
import { GET_CLASH_COUNT } from '../../api/graphQL/queries/bestPracticeClash.queries'

interface UseBestPracticeClashDetectionResult {
    clashCount: number
    showWarning: boolean
    setShowWarning: (show: boolean) => void
    loading: boolean
    checkForClashes: () => void
    resetClashState: () => void
}

/**
 * Hook to detect and manage Best Practice clashes after BP application.
 * Provides checkForClashes() to trigger after BP completes and resetClashState() to cleanup.
 *
 * @param projectId - The project ID to check for clashes
 * @param treePath - Tree path to filter clashes for specific family
 */
export const useBestPracticeClashDetection = (
    projectId: string,
    treePath?: string
): UseBestPracticeClashDetectionResult => {
    const [showWarning, setShowWarning] = useState(false)
    const [clashCount, setClashCount] = useState(0)

    const [getClashCount, { loading }] = useLazyQuery(GET_CLASH_COUNT, {
        variables: {
            input: {
                projectId: projectId,
                treePath: treePath,
            },
        },
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const count = data?.bestPracticeClashCount ?? 0
            setClashCount(count)

            // Only show warning if clashes detected
            if (count > 0) {
                setShowWarning(true)
            }
        },
    })

    const checkForClashes = () => {
        // Only check for clashes if SmartProjectCopy feature flag is enabled
        if (
            process.env.REACT_APP_EnableSmartProjectCopy === '1' &&
            projectId &&
            treePath
        ) {
            getClashCount()
        }
    }

    const resetClashState = () => {
        setShowWarning(false)
        setClashCount(0)
    }

    return {
        clashCount,
        showWarning,
        setShowWarning,
        loading,
        checkForClashes,
        resetClashState,
    }
}
